import React, { useState, useEffect, useRef } from 'react';
import styles from './A_SocialInput.module.css';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';

interface SocialPlatform {
  name: string;
  iconComponent: keyof typeof Q_Icon;
  patterns: RegExp[];
  extractId: (url: string) => string;
  urlTemplate?: string;
}

const socialPlatforms: SocialPlatform[] = [
  {
    name: 'Facebook',
    iconComponent: 'SocialFacebookIcon',
    patterns: [/facebook\.com\/([^/?]+)/, /fb\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/facebook\.com\/([^/?]+)|fb\.com\/([^/?]+)/);
      return match?.[1] || match?.[2] || '';
    },
    urlTemplate: 'https://facebook.com/{id}',
  },
  {
    name: 'Twitter/X',
    iconComponent: 'SocialTwitterIcon',
    patterns: [/twitter\.com\/([^/?]+)/, /x\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/twitter\.com\/([^/?]+)|x\.com\/([^/?]+)/);
      return match?.[1] || match?.[2] || '';
    },
    urlTemplate: 'https://twitter.com/{id}',
  },
  {
    name: 'Instagram',
    iconComponent: 'SocialInstagramIcon',
    patterns: [/instagram\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/instagram\.com\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://instagram.com/{id}',
  },
  {
    name: 'LinkedIn',
    iconComponent: 'SocialLinkedInIcon',
    patterns: [/linkedin\.com\/(?:in\/)?([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/linkedin\.com\/(?:in\/)?([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://www.linkedin.com/in/{id}',
  },
  {
    name: 'Google',
    iconComponent: 'SocialGoogleIcon',
    patterns: [/google\.com/],
    extractId: () => 'Google',
    urlTemplate: 'https://google.com',
  },
  {
    name: 'YouTube',
    iconComponent: 'SocialYoutubeIcon',
    patterns: [
      /youtube\.com\/channel\/([^/?]+)/,
      /youtube\.com\/c\/([^/?]+)/,
      /youtube\.com\/user\/([^/?]+)/,
    ],
    extractId: (url: string) => {
      const match = url.match(/youtube\.com\/(?:channel\/|c\/|user\/)([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://youtube.com/{id}',
  },
  {
    name: 'Apple',
    iconComponent: 'SocialAppleIcon',
    patterns: [/apple\.com/],
    extractId: () => 'Apple',
    urlTemplate: 'https://apple.com',
  },
  {
    name: 'Snapchat',
    iconComponent: 'SocialSnapchatIcon',
    patterns: [/snapchat\.com\/add\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/snapchat\.com\/add\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://snapchat.com/add/{id}',
  },
  {
    name: 'Pinterest',
    iconComponent: 'SocialPinterestIcon',
    patterns: [/pinterest\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/pinterest\.com\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://pinterest.com/{id}',
  },
  {
    name: 'Medium',
    iconComponent: 'SocialMediumIcon',
    patterns: [/medium\.com\/@([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/medium\.com\/@([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://medium.com/@{id}',
  },
  {
    name: 'Telegram',
    iconComponent: 'SocialTelegramIcon',
    patterns: [/t\.me\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/t\.me\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://t.me/{id}',
  },
  {
    name: 'TikTok',
    iconComponent: 'SocialTikTokIcon',
    patterns: [/tiktok\.com\/@([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/tiktok\.com\/@([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://www.tiktok.com/@{id}',
  },
  {
    name: 'Discord',
    iconComponent: 'SocialDiscordIcon',
    patterns: [/discord\.gg\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/discord\.gg\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://discord.gg/{id}',
  },
  {
    name: 'GitHub',
    iconComponent: 'SocialGithubIcon',
    patterns: [/github\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/github\.com\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://github.com/{id}',
  },
  {
    name: 'Reddit',
    iconComponent: 'SocialRedditIcon',
    patterns: [/reddit\.com\/user\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/reddit\.com\/user\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://reddit.com/user/{id}',
  },
  {
    name: 'Spotify',
    iconComponent: 'SocialSpotifyIcon',
    patterns: [/open\.spotify\.com\/user\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/open\.spotify\.com\/user\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://open.spotify.com/user/{id}',
  },
  {
    name: 'VK',
    iconComponent: 'SocialVKIcon',
    patterns: [/vk\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/vk\.com\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://vk.com/{id}',
  },
  {
    name: 'WhatsApp',
    iconComponent: 'SocialWhatsAppIcon',
    patterns: [/wa\.me\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/wa\.me\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://wa.me/{id}',
  },
  {
    name: 'Twitch',
    iconComponent: 'SocialTwitchIcon',
    patterns: [/twitch\.tv\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/twitch\.tv\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://twitch.tv/{id}',
  },
  {
    name: 'Figma',
    iconComponent: 'SocialFigmaIcon',
    patterns: [/figma\.com\/@([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/figma\.com\/@([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://www.figma.com/@{id}',
  },
  {
    name: 'Dribbble',
    iconComponent: 'SocialDribbbleIcon',
    patterns: [/dribbble\.com\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/dribbble\.com\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://dribbble.com/{id}',
  },
  {
    name: 'Notion',
    iconComponent: 'SocialNotionIcon',
    patterns: [/notion\.so/],
    extractId: () => 'Notion',
    urlTemplate: 'https://www.notion.so',
  },
  {
    name: 'Яндекс.Дзен',
    iconComponent: 'SocialDzenIcon',
    patterns: [/zen\.yandex\.ru\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/zen\.yandex\.ru\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://zen.yandex.ru/{id}',
  },
  {
    name: 'Одноклассники',
    iconComponent: 'SocialOKIcon',
    patterns: [/ok\.ru\/([^/?]+)/],
    extractId: (url: string) => {
      const match = url.match(/ok\.ru\/([^/?]+)/);
      return match?.[1] || '';
    },
    urlTemplate: 'https://ok.ru/{id}',
  },
];

interface A_SocialInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  originalValue?: string;
  platformIcon?: string;
}

export default function A_SocialInput({
  value = '',
  onChange,
  placeholder,
  className,
  originalValue,
  platformIcon,
}: A_SocialInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [detectedPlatform, setDetectedPlatform] =
    useState<SocialPlatform | null>(null);
  const rawValueRef = useRef(value);
  const { language } = useLanguage();

  const findPlatformByIcon = (icon?: string) =>
    icon
      ? socialPlatforms.find(platform => platform.iconComponent === icon) ||
        null
      : null;

  const matchPlatformByUrl = (url: string) => {
    if (!url) return null;
    for (const platform of socialPlatforms) {
      for (const pattern of platform.patterns) {
        if (pattern.test(url)) {
          return platform;
        }
      }
    }
    return null;
  };

  const extractDisplayValue = (
    url: string,
    platform: SocialPlatform | null
  ) => {
    if (!url) return '';
    if (url.startsWith('@')) return url.substring(1);
    if (platform) {
      const id = platform.extractId(url);
      if (id) return id;
    }
    const trimmed = url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
    const parts = trimmed.split('/');
    return parts[parts.length - 1] || trimmed;
  };

  const rebuildUrl = (
    currentValue: string,
    platform: SocialPlatform,
    newId: string
  ) => {
    const sanitizedId = newId.trim();
    if (!sanitizedId) return '';
    if (currentValue) {
      const existingId = platform.extractId(currentValue);
      if (existingId) {
        const idx = currentValue.lastIndexOf(existingId);
        if (idx >= 0) {
          return (
            currentValue.slice(0, idx) +
            sanitizedId +
            currentValue.slice(idx + existingId.length)
          );
        }
      }
    }
    if (platform.urlTemplate) {
      return platform.urlTemplate.replace('{id}', sanitizedId);
    }
    return sanitizedId;
  };

  useEffect(() => {
    const sourceValue = value || originalValue || '';
    rawValueRef.current = sourceValue;
    const platform =
      findPlatformByIcon(platformIcon) || matchPlatformByUrl(sourceValue);
    setDetectedPlatform(platform);
    setInputValue(extractDisplayValue(sourceValue, platform));
  }, [value, originalValue, platformIcon]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const sanitized = rawInput.startsWith('@')
      ? rawInput.substring(1)
      : rawInput;
    const platform =
      detectedPlatform || matchPlatformByUrl(rawValueRef.current);

    if (!sanitized) {
      setInputValue('');
      rawValueRef.current = '';
      onChange?.('');
      setDetectedPlatform(
        platformIcon ? findPlatformByIcon(platformIcon) : null
      );
      return;
    }

    let nextValue = sanitized;
    if (platform) {
      nextValue = rebuildUrl(rawValueRef.current, platform, sanitized);
    }

    rawValueRef.current = nextValue;
    setInputValue(sanitized);
    onChange?.(nextValue);

    const updatedPlatform =
      findPlatformByIcon(platformIcon) || matchPlatformByUrl(nextValue);
    setDetectedPlatform(updatedPlatform);
  };

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.input}>
        <div className={styles.platform_icon}>
          {detectedPlatform ? (
            React.createElement(Q_Icon[detectedPlatform.iconComponent], {
              width: 24,
              height: 24,
            })
          ) : (
            <Q_Icon.SocialOtherIcon width={24} height={24} />
          )}
        </div>
        <input
          type='text'
          value={inputValue}
          onChange={handleInputChange}
          placeholder={
            placeholder ||
            getTranslation(language as any, 'socialInput.placeholder')
          }
          className={cn(styles.display_input, 'text-p-7')}
        />
      </div>
    </div>
  );
}
